import type { OptionType, SelectMeta } from '../types'

import { useMemo, useState } from 'react'
import { flatten, orderBy, partition } from 'lodash/fp'
import { XCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/shared/components/ui/command'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'

type FilterManagerProps = {
	options: OptionType[]
	onChange(value: string[], meta: SelectMeta): void
	value: string[]
}

export function FilterManager(props: FilterManagerProps) {
	const { t } = useTranslation()

	const [searchText, setSearchText] = useState('')
	const options = useMemo(
		() => getOptions(props.value, props.options),
		[props.value, props.options]
	)
	const filteredOptions = useMemo(
		() =>
			options?.filter((option) =>
				option.label.toLowerCase().includes(searchText.toLowerCase())
			) || [],
		[options, searchText]
	)
	const handleChange = (meta: SelectMeta) => props.onChange(props.value, meta)

	return (
		<Command>
			<CommandInput
				onValueChange={setSearchText}
				placeholder={t('general_search', { defaultValue: 'Search' })}
				autoFocus
				className='h-9'
			/>
			<Virtualized asChild>
				<CommandList>
					<CommandEmpty>
						{t('general_no_results_found', {
							defaultValue: 'No results found',
						})}
					</CommandEmpty>
					<CommandGroup>
						<VirtualizedVirtualizer>
							{(filteredOptions as OptionType[]).map((option) => {
								const isSelected = props.value.some((v) => v === option.value)

								return (
									<CommandItem
										key={option.value}
										value={option.value}
										keywords={[option.label]}
										onSelect={() => {
											isSelected
												? handleChange({
														action: 'deselect-option',
														option: option.value,
													})
												: handleChange({
														action: 'select-option',
														option: option.value,
													})
										}}
									>
										<Checkbox checked={isSelected} onChange={() => {}} />
										{option.label}
									</CommandItem>
								)
							})}
						</VirtualizedVirtualizer>
					</CommandGroup>
					<CommandSeparator className='sticky bottom-10' />
					<CommandGroup className='sticky bottom-0 z-10 bg-popover'>
						<CommandItem
							onSelect={() =>
								handleChange({
									action: 'clear-options',
								})
							}
						>
							<XCircleIcon />
							{t('app.remove_all_filters', {
								defaultValue: 'Remove all filters',
							})}
						</CommandItem>
					</CommandGroup>
				</CommandList>
			</Virtualized>
		</Command>
	)
}

const getOptions = (
	selected: string[],
	options: OptionType[]
): OptionType[] => {
	if (!options || !options.length)
		return orderBy<OptionType>(['label'], ['asc'], options)

	return flatten(
		partition(
			({ value }) => selected.includes(value),
			orderBy<OptionType>(['label'], ['asc'], options)
		)
	)
}
